import request from '#/utils/request';
import { keysToSnake } from '#/api/adapters/wireCase';
import {
  parseVer4DetailResponse,
  parseVer4ListResponse,
} from '#/api/adapters/envelope';

/**
 * 兩種採購單共用的異動單 HTTP（`/srm/{type}/{id}/amendment/*`）。
 * DRAFT → SUBMITTED → APPROVED｜REJECTED；只有 approve 會套用採購變更與付款計畫。
 *
 * @param {string} basePath 例：`/srm/purchase-order`
 * @param {object} options
 * @param {(payload: object) => object} options.serializeWrite 建立／修改草稿 body 序列化（回 snake）
 */
export function createPurchaseAmendmentApi(basePath, { serializeWrite }) {
  const collectionPath = (orderId) => `${basePath}/${orderId}/amendment`;
  const itemPath = (orderId, amendmentId) => `${collectionPath(orderId)}/${amendmentId}`;

  async function postCommand(orderId, amendmentId, command, payload) {
    const { data: raw } = await request.post(
      `${itemPath(orderId, amendmentId)}/${command}`,
      keysToSnake(payload ?? {}),
    );
    return parseVer4DetailResponse(raw);
  }

  return {
    /** GET 列表 → `{ data: Amendment[] }` */
    async fetchList(orderId) {
      const { data: raw } = await request.get(collectionPath(orderId));
      return parseVer4ListResponse(raw);
    },
    /** GET 明細（含 before／after、paymentPlan、paymentPlanProjection） */
    async fetchDetail(orderId, amendmentId) {
      const { data: raw } = await request.get(itemPath(orderId, amendmentId));
      return parseVer4DetailResponse(raw);
    },
    /** POST 建立 DRAFT；`updatedAt` 為採購單最新版本。回應 `data` 是異動單，不是採購單。 */
    async create(orderId, payload) {
      const { data: raw } = await request.post(
        collectionPath(orderId),
        serializeWrite(payload),
      );
      return parseVer4DetailResponse(raw);
    },
    /** PATCH 修改 DRAFT；`updatedAt` 為異動單最新版本。 */
    async update(orderId, amendmentId, payload) {
      const { data: raw } = await request.patch(
        itemPath(orderId, amendmentId),
        serializeWrite(payload),
      );
      return parseVer4DetailResponse(raw);
    },
    /** DELETE 取消 DRAFT；body `{ updatedAt }`。 */
    async remove(orderId, amendmentId, payload = {}) {
      const { data: raw } = await request.delete(
        itemPath(orderId, amendmentId),
        { data: keysToSnake(payload) },
      );
      return parseVer4DetailResponse(raw ?? {});
    },
    /** POST submit；body `{ updatedAt }`。 */
    submit(orderId, amendmentId, payload) {
      return postCommand(orderId, amendmentId, 'submit', payload);
    },
    /** POST reject；body `{ rejectReason, updatedAt }`；需 review 權限。 */
    reject(orderId, amendmentId, payload) {
      return postCommand(orderId, amendmentId, 'reject', payload);
    },
    /** POST approve；body `{ updatedAt }`；需 review 權限。 */
    approve(orderId, amendmentId, payload) {
      return postCommand(orderId, amendmentId, 'approve', payload);
    },
  };
}
